import { useCallback } from "react"
import {
  GetDashboardDocument,
  UpdateAlertStatusDocument,
  UpdateAlertStatusMutation,
  UpdateAlertStatusMutationVariables,
  AlertStatusEnum,
  AlertTypeEnum,
  AlertSeverityEnum,
  AlertSourceEnum,
  GetSystemAlertsDocument,
} from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"

export type AlertStatus = "new" | "acknowledged" | "attended" | "on_hold" | "completed"

export function useUpdateAlertStatus(dashboardId?: string, systemId?: string) {
  const refetchQueries = []
  if (dashboardId) refetchQueries.push({ query: GetDashboardDocument, variables: { dashboardId } })
  if (systemId) refetchQueries.push({ query: GetSystemAlertsDocument, variables: { systemId } })
  const [updateAlertStatus, { loading }] = useMutationWithEndpoint<
    UpdateAlertStatusMutation,
    UpdateAlertStatusMutationVariables
  >(
    UpdateAlertStatusDocument,
    {
      refetchQueries,
      optimisticResponse: (variables) => ({
        updateAlertStatus: {
          __typename: "AlertFull" as const,
          id: variables.input.id,
          status: variables.input.status,
          name: "",
          type: AlertTypeEnum.Status,
          severity: AlertSeverityEnum.Info,
          source: AlertSourceEnum.Internal,
          installation: null,
          installation_name: null,
          installation_group_id: null,
          installation_group_name: null,
          message: "",
          metadata: null,
          fired_at: "",
          resolved_at: null,
          created_at: "",
          assets: [],
          asset_count: 0,
          events: [],
          severity_rank: null,
          status_rank: null,
          hours_since_fired: null,
          resolution_time_hours: null,
          customer_site_context: null,
          system_count: 0,
          systems: [],
        },
      }),
      update: (cache, { data }) => {
        if (data?.updateAlertStatus) {
          cache.modify({
            fields: {
              getAlerts(existingAlerts = {}) {
                return existingAlerts
              },
            },
          })
        }
      },
    },
    "primary",
  )

  const updateStatus = useCallback(
    async (alertId: string, status: AlertStatus, note?: string) => {
      try {
        await updateAlertStatus({
          variables: {
            input: {
              id: alertId,
              status: status as AlertStatusEnum,
              note,
            },
          },
        })
      } catch (error) {
        toaster.create({
          title: "Failed to update alert status",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          type: "error",
          duration: 5000,
        })
        throw error
      }
    },
    [updateAlertStatus],
  )

  return { updateStatus, loading }
}
